// hooks/useWishlist.js
// Wishlist Hook - Synchronized with Mobile App
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useMemo } from "react";

export const useWishlist = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Fetch wishlist data - only if user is authenticated
  const {
    data: wishlistData = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["wishlist"],
    queryFn: async () => {
      try {
        const response = await apiService.getWishlist();
        return response.data || [];
      } catch (error) {
        // Don't log errors if user is not authenticated - this is expected
        if (isAuthenticated) {
          console.log("Failed to fetch wishlist:", error.message);
        }
        return [];
      }
    },
    enabled: !!isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 1,
  });

  // Get wishlist product IDs as a Set for easy lookup
  const wishlistProductIds = useMemo(() => {
    return new Set(
      wishlistData.map((item) => item.product?.id || item.product?._id || item.id || item._id)
    );
  }, [wishlistData]);

  // Check if a product is in wishlist
  const isInWishlist = (productId) => {
    return wishlistProductIds.has(productId);
  };

  // Add to wishlist mutation
  const addToWishlistMutation = useMutation({
    mutationFn: (productId) => apiService.addToWishlist(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist"] });
    },
  });

  // Remove from wishlist mutation
  const removeFromWishlistMutation = useMutation({
    mutationFn: (productId) => apiService.removeFromWishlist(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist"] });
    },
  });

  // Clear wishlist mutation
  const clearWishlistMutation = useMutation({
    mutationFn: () => apiService.clearWishlist(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist"] });
    },
  });

  // Toggle wishlist - add or remove based on current state
  const toggleWishlist = async (productId) => {
    if (!isAuthenticated) {
      throw new Error("Please login to add items to your wishlist");
    }
    
    try {
      if (isInWishlist(productId)) {
        await removeFromWishlistMutation.mutateAsync(productId);
        return { action: "removed", success: true };
      } else {
        await addToWishlistMutation.mutateAsync(productId);
        return { action: "added", success: true };
      }
    } catch (error) {
      // Parse error message for user-friendly feedback
      let errorMessage = "Failed to update wishlist";
      
      if (error.message?.includes("phoneNumber")) {
        errorMessage = "Please update your phone number in your profile first";
      } else if (error.message?.includes("validation")) {
        errorMessage = "Please complete your profile to use wishlist";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error("Wishlist error:", errorMessage);
      throw new Error(errorMessage);
    }
  };

  return {
    wishlist: wishlistData,
    wishlistProductIds,
    wishlistCount: wishlistData.length,
    isInWishlist,
    isLoading,
    error,
    refetch,
    addToWishlist: addToWishlistMutation.mutateAsync,
    removeFromWishlist: removeFromWishlistMutation.mutateAsync,
    clearWishlist: clearWishlistMutation.mutate,
    toggleWishlist,
    isAddingToWishlist: addToWishlistMutation.isPending,
    isRemovingFromWishlist: removeFromWishlistMutation.isPending,
    isToggling:
      addToWishlistMutation.isPending || removeFromWishlistMutation.isPending,
  };
};

export default useWishlist;

